import React, { useState, useEffect, useCallback, useRef } from 'react';

import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  MenuItem,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import dayjs from 'dayjs';
import { useHistory } from 'react-router';

import api from '../../utils/functions/api';

const apiBase = String(process.env.REACT_APP_API_URL || '').trim();

const SCOPE_OPTIONS = [
  { value: 'commitment', label: 'เฉพาะโครงการที่ยังมีภาระผูกพัน' },
  { value: 'commitment_or_money', label: 'มีภาระผูกพัน หรือมีเงินค้างจ่าย/ค้างรับ' },
  { value: 'all', label: 'ทุกโครงการ' },
];

// สวิตช์เปิด/ปิดระบบ AI ติดตามโครงการ + งบตัวเสียเงินต่อวัน — cron ตั้งไว้ทุกวันเสมอ ปิดอยู่ = ข้ามรอบ ไม่เรียก AI
export default function DeveloperAiFollowup() {
  const [settings, setSettings] = useState(null);
  const [budget, setBudget] = useState('');
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pendingCount, setPendingCount] = useState(null); // null = ไม่ใช่ผู้มีอำนาจ (ไม่แสดงตัวเลข)
  const history = useHistory();

  // สั่งรันด้วยมือ: เก็บภาพข้อมูล → AI วิเคราะห์ทุกโครงการ → สร้างรายงาน (รันเบื้องหลังที่ server, หน้านี้ poll สถานะ)
  const [job, setJob] = useState(null);
  const [runConfirmOpen, setRunConfirmOpen] = useState(false);
  const wasRunning = useRef(false);

  const pollJob = useCallback(async () => {
    try {
      const { data } = await api.get(`${apiBase}/ai-followup/run-all/status`);
      setJob(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const me = await api.get(`${apiBase}/ai-followup/me`);
      if (me.data?.is_authority) {
        const { data: pending } = await api.get(`${apiBase}/ai-followup/questions-pending-review`);
        setPendingCount((pending || []).length);
      }
    } catch {
      setPendingCount(null);
    }
    try {
      const { data } = await api.get(`${apiBase}/ai-followup/settings`);
      setSettings(data);
      setBudget(String(data.daily_budget_baht));
      setRate(String(data.paid_baht_per_1k));
    } catch {
      setError('โหลดการตั้งค่าไม่สำเร็จ');
    }
  }, []);

  useEffect(() => {
    load();
    pollJob();
  }, [load, pollJob]);

  // ระหว่างรันถามสถานะทุก 3 วินาที พอรันจบโหลดจำนวนคำถามรอตรวจใหม่
  useEffect(() => {
    if (job?.running) {
      wasRunning.current = true;
      const id = setInterval(pollJob, 3000);
      return () => clearInterval(id);
    }
    if (wasRunning.current) {
      wasRunning.current = false;
      load();
    }
    return undefined;
  }, [job?.running, pollJob, load]);

  const startRun = async () => {
    setRunConfirmOpen(false);
    setError('');
    try {
      const { data } = await api.post(`${apiBase}/ai-followup/run-all`);
      setJob(data);
    } catch (e) {
      setError(e?.response?.data?.error?.message || e?.message || 'สั่งรันไม่สำเร็จ');
    }
  };

  const STAGE_LABEL = { snapshot: 'กำลังเก็บภาพข้อมูลโครงการ', analyze: 'AI กำลังวิเคราะห์โครงการ', alert: 'กำลังสร้างรายงานผู้บริหาร' };

  const save = async (patch) => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put(`${apiBase}/ai-followup/settings`, patch);
      setSettings(data);
      setBudget(String(data.daily_budget_baht));
      setRate(String(data.paid_baht_per_1k));
    } catch (e) {
      setError(e?.response?.data?.error?.message || e?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const numbersChanged = settings
    && (Number(budget) !== settings.daily_budget_baht || Number(rate) !== settings.paid_baht_per_1k);

  return (
    <Card sx={{ mb: 3 }} variant="outlined">
      <CardHeader
        title="AI ติดตามโครงการ"
        subheader="ใช้ AI ตัวฟรีก่อน ถ้าโควตาหมดจึงใช้ตัวเสียเงิน ไม่เกินงบต่อวันที่ตั้งไว้ — ปิดอยู่ระบบจะไม่เรียก AI เลย"
      />
      <CardContent>
        {!settings && !error && <CircularProgress size={20} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {settings && (
          <Stack spacing={2}>
            <FormControlLabel
              control={(
                <Switch
                  checked={settings.enabled}
                  disabled={saving}
                  onChange={(e) => save({ enabled: e.target.checked })}
                />
              )}
              label={settings.enabled ? 'เปิดอยู่ (งานรายวันทำงานตามเวลา)' : 'ปิดอยู่'}
            />
            <TextField
              select
              size="small"
              label="ขอบเขตโครงการที่ AI ติดตาม"
              value={settings.scope}
              disabled={saving || Boolean(job?.running)}
              onChange={(e) => save({ scope: e.target.value })}
              sx={{ maxWidth: 420 }}
            >
              {SCOPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={(
                <Switch
                  checked={settings.question_review}
                  disabled={saving}
                  onChange={(e) => save({ question_review: e.target.checked })}
                />
              )}
              label={settings.question_review
                ? 'หัวหน้าตรวจคำถามก่อนแสดงให้เจ้าหน้าที่: เปิด (คำถามใหม่ต้องรออนุมัติ)'
                : 'หัวหน้าตรวจคำถามก่อนแสดงให้เจ้าหน้าที่: ปิด (คำถามใหม่แสดงทันที)'}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                type="number"
                label="งบตัวเสียเงินต่อวัน (บาท)"
                value={budget}
                inputProps={{ min: 0 }}
                onChange={(e) => setBudget(e.target.value)}
                sx={{ width: 220 }}
              />
              <TextField
                size="small"
                type="number"
                label="ราคาโดยประมาณ (บาท/1,000 token)"
                value={rate}
                inputProps={{ min: 0, step: 0.01 }}
                onChange={(e) => setRate(e.target.value)}
                sx={{ width: 260 }}
              />
              <Button
                variant="contained"
                disabled={saving || !numbersChanged}
                onClick={() => save({ daily_budget_baht: Number(budget), paid_baht_per_1k: Number(rate) })}
              >
                บันทึก
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" onClick={() => history.push('/director/executive-alerts')}>
                เปิดหน้า Executive Alerts
              </Button>
              {pendingCount !== null && (
                <Typography variant="body2" color={pendingCount > 0 ? 'warning.main' : 'text.secondary'}>
                  {pendingCount > 0
                    ? `มีคำถามจาก AI รอคุณตรวจ ${pendingCount} ข้อ (อนุมัติได้ในหน้านั้น)`
                    : 'ไม่มีคำถามจาก AI ที่รอตรวจ'}
                </Typography>
              )}
            </Stack>
            {pendingCount !== null && (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={!settings.enabled || Boolean(job?.running)}
                    onClick={() => setRunConfirmOpen(true)}
                    startIcon={job?.running ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {job?.running ? 'กำลังรัน...' : 'สั่งรันตอนนี้'}
                  </Button>
                  {!settings.enabled && (
                    <Typography variant="caption" color="text.secondary">เปิดสวิตช์ด้านบนก่อนจึงสั่งรันได้</Typography>
                  )}
                </Stack>
                {job?.running && (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">
                      {job.stage === 'analyze'
                        ? `AI กำลังวิเคราะห์โครงการ ${job.analyzed + job.failed} จาก ${job.total} โครงการ`
                          + `${job.failed ? ` (ล้มเหลว ${job.failed})` : ''} — ยิงถามทีละโครงการ ทุก 15 วินาที`
                        : `${STAGE_LABEL[job.stage] || 'กำลังทำงาน'} (ข้อมูลวันที่ ${job.date})`}
                    </Typography>
                  </Stack>
                )}
                {job && !job.running && job.finished_at && (
                  <Alert severity={job.error ? 'error' : 'success'}>
                    {job.error
                      ? `รันไม่สำเร็จ: ${job.error}`
                      : `รันเสร็จแล้ว (ข้อมูลวันที่ ${job.date}): วิเคราะห์สำเร็จ ${job.analyzed} โครงการ ล้มเหลว ${job.failed}`
                        + `${job.stopped_reason ? ` — หยุดเพราะ ${job.stopped_reason}` : ''}`}
                  </Alert>
                )}
              </Stack>
            )}
            {settings.updated_by && (
              <Typography variant="caption" color="text.secondary">
                แก้ล่าสุดโดย {settings.updated_by} เมื่อ {dayjs(settings.updated_at).format('D/M/YYYY HH:mm')}
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
      <Dialog open={runConfirmOpen} onClose={() => setRunConfirmOpen(false)}>
        <DialogTitle>สั่งรัน AI ติดตามโครงการตอนนี้</DialogTitle>
        <DialogContent>
          <Typography>
            ระบบจะเก็บภาพข้อมูลของเมื่อวาน ให้ AI วิเคราะห์ทุกโครงการที่ยังมีภาระผูกพัน แล้วสร้างรายงานผู้บริหาร
            ใช้เวลาหลายนาทีและใช้โควตา AI จริง (ตัวฟรีก่อน ตัวเสียเงินไม่เกินงบต่อวัน) ยืนยันสั่งรันใช่ไหม?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus color="inherit" onClick={() => setRunConfirmOpen(false)}>ยกเลิก</Button>
          <Button variant="contained" color="warning" onClick={startRun}>ยืนยันสั่งรัน</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
